import React, { useState, useEffect } from 'react';
import api from '../../api';
import './TutorialsTab.css';

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || 'C';

export default function PlayerTutorialsTab() {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [playingId, setPlayingId] = useState(null); // the video whose player is open (the others show a thumbnail)

  // Fetch videos
  useEffect(() => {
    loadVideos();
    loadCategories();
  }, [searchTerm, selectedCategory]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await api.get(`/videos?${params}`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/videos/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  return (
    <div className="tutorials-container tutorials-player">
      <div className="tp-hero">
        <div>
          <h2 className="tp-title">Video Tutorials</h2>
          <p className="tp-sub">Short lessons from your coach to watch between sessions.</p>
        </div>
        {!loading && <span className="tp-count">{videos.length} {videos.length === 1 ? 'video' : 'videos'}</span>}
      </div>

      <div className="tp-search">
        <span className="tp-search-icon" aria-hidden="true">🔍</span>
        <input
          type="text"
          className="tp-search-input"
          placeholder="Search by title"
          aria-label="Search videos by title"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button type="button" className="tp-search-clear" aria-label="Clear search" onClick={() => setSearchTerm('')}>✕</button>
        )}
      </div>

      {categories.length > 0 && (
        <div className="tp-chips" aria-label="Categories">
          <button
            type="button"
            className={`tp-chip ${selectedCategory === '' ? 'is-active' : ''}`}
            onClick={() => setSelectedCategory('')}
          >All</button>
          {categories.map(cat => (
            <button
              key={cat}
              type="button"
              className={`tp-chip ${selectedCategory === cat ? 'is-active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
            >{cat}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="tp-grid" aria-busy="true">
          {[0, 1].map(i => (
            <div key={i} className="tp-card tp-skeleton">
              <div className="tp-thumb" />
              <div className="tp-body"><div className="tp-line" /><div className="tp-line tp-line--short" /></div>
            </div>
          ))}
        </div>
      ) : videos.length === 0 ? (
        <div className="tp-empty">
          <div className="tp-empty-icon" aria-hidden="true">🎬</div>
          <div className="tp-empty-title">{searchTerm || selectedCategory ? 'No videos match' : 'No videos yet'}</div>
          <div className="tp-empty-sub">
            {searchTerm || selectedCategory ? 'Try another word or choose All.' : 'Your coach has not added any tutorials yet.'}
          </div>
        </div>
      ) : (
        <div className="tp-grid">
          {videos.map(video => (
            <article key={video._id} className="tp-card">
              <div className="tp-media">
                {playingId === video._id ? (
                  <iframe
                    className="tp-iframe"
                    src={`https://www.youtube.com/embed/${video.youtubeVideoId}?autoplay=1&rel=0`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button type="button" className="tp-thumb" onClick={() => setPlayingId(video._id)} aria-label={`Play ${video.title}`}>
                    <img src={`https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`} alt="" loading="lazy" />
                    <span className="tp-play" aria-hidden="true"><span className="tp-play-tri" /></span>
                  </button>
                )}
              </div>
              <div className="tp-body">
                {video.categories && video.categories.length > 0 && (
                  <div className="tp-tags">
                    {video.categories.map(cat => <span key={cat} className="tp-tag">{cat}</span>)}
                  </div>
                )}
                <h3 className="tp-video-title">{video.title}</h3>
                <div className="tp-coach">
                  <span className="tp-avatar" aria-hidden="true">{initials(video.coachId && video.coachId.name)}</span>
                  <span>{video.coachId && video.coachId.name ? video.coachId.name : 'Your coach'}</span>
                </div>
                {video.description && <p className="tp-desc">{video.description}</p>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
